// src/app/modules/Events/pages/EventForm.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import * as actions from '../../Dashbord/_redux/dashboard/Actions';
import './events.scss';

const EMPTY_TIER = { name: '', price: '', quantity: '' };

function Field({ label, required, children }) {
  return (
    <div className="ev-field">
      <label className="ev-field__label">{label}{required && <span style={{ color: '#DC2626' }}> *</span>}</label>
      {children}
    </div>
  );
}

export function EventForm() {
  const { eventId } = useParams();
  const isEdit = !!eventId;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { org }    = useSelector(s => ({ org: s.auth.organization }), shallowEqual);
  const { ev, loading } = useSelector(s => ({
    ev:      s.dashboard.eventDetail,
    loading: s.dashboard.actionsLoading,
  }), shallowEqual);

  const [form, setForm] = useState({
    name: '', description: '', venue: '', start_date: '', end_date: '', capacity: '',
  });
  const [tiers, setTiers] = useState([{ ...EMPTY_TIER }]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && eventId) dispatch(actions.fetchAllData('event', eventId));
  }, [eventId]);

  useEffect(() => {
    if (isEdit && ev) {
      setForm({
        name:        ev.name        || '',
        description: ev.description || '',
        venue:       ev.venue       || '',
        start_date:  ev.start_date  ? ev.start_date.slice(0, 16) : '',
        end_date:    ev.end_date    ? ev.end_date.slice(0, 16)   : '',
        capacity:    ev.capacity    || '',
      });
      if (ev.tiers?.length) setTiers(ev.tiers.map(t => ({ name: t.name, price: t.price, quantity: t.quantity ?? '' })));
    }
  }, [ev]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setTier = (i, key, val) => setTiers(ts => ts.map((t, idx) => idx === i ? { ...t, [key]: val } : t));
  const addTier = () => setTiers(ts => [...ts, { ...EMPTY_TIER }]);
  const removeTier = (i) => setTiers(ts => ts.filter((_, idx) => idx !== i));

  const validate = () => {
    const e = {};
    if (!form.name.trim())       e.name       = 'Event name is required';
    if (!form.start_date)        e.start_date = 'Start date is required';
    if (!form.end_date)          e.end_date   = 'End date is required';
    if (tiers.some(t => !t.name.trim()))  e.tiers = 'All tier names are required';
    if (tiers.some(t => !t.price))        e.tiers = 'All tier prices are required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      ...form,
      organization_id: org.organization_id,
      capacity:  form.capacity ? Number(form.capacity) : undefined,
      tiers: tiers.map(t => ({
        name:     t.name,
        price:    Number(t.price),
        quantity: t.quantity ? Number(t.quantity) : null,
      })),
    };

    try {
      if (isEdit) {
        await dispatch(actions.addData('update_event', payload, eventId));
        navigate(`/events/${eventId}`);
      } else {
        const created = await dispatch(actions.addData('create_event', payload));
        navigate(created?.event_id ? `/events/${created.event_id}` : '/events/list');
      }
    } catch (_) { /* toast shown in Actions */ }
  };

  return (
    <div className="ev-page">
      <div className="ev-page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="ev-back" onClick={() => navigate(isEdit ? `/events/${eventId}` : '/events/list')}>
            <ArrowLeft size={16} strokeWidth={1.8} />
          </button>
          <div>
            <h1 className="ev-page__title">{isEdit ? 'Edit Event' : 'Create Event'}</h1>
          </div>
        </div>
      </div>

      <form className="ev-form" onSubmit={handleSubmit}>
        <div className="ev-form__section">
          <h3 className="ev-form__section-title">Event Info</h3>
          <div className="ev-form__grid">
            <Field label="Event Name" required>
              <input
                className={`ev-input ${errors.name ? 'error' : ''}`}
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. Lagos Jazz Night"
              />
              {errors.name && <span className="ev-error">{errors.name}</span>}
            </Field>
            <Field label="Venue">
              <input className="ev-input" value={form.venue} onChange={e => set('venue', e.target.value)} placeholder="e.g. Eko Hotel, Lagos" />
            </Field>
            <Field label="Start Date & Time" required>
              <input type="datetime-local" className={`ev-input ${errors.start_date ? 'error' : ''}`} value={form.start_date} onChange={e => set('start_date', e.target.value)} />
              {errors.start_date && <span className="ev-error">{errors.start_date}</span>}
            </Field>
            <Field label="End Date & Time" required>
              <input type="datetime-local" className={`ev-input ${errors.end_date ? 'error' : ''}`} value={form.end_date} onChange={e => set('end_date', e.target.value)} />
              {errors.end_date && <span className="ev-error">{errors.end_date}</span>}
            </Field>
            <Field label="Total Capacity">
              <input type="number" className="ev-input" value={form.capacity} onChange={e => set('capacity', e.target.value)} placeholder="Leave blank for unlimited" min={1} />
            </Field>
          </div>
          <Field label="Description">
            <textarea className="ev-input ev-textarea" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell attendees what to expect" />
          </Field>
        </div>

        {/* Tiers */}
        <div className="ev-form__section">
          <h3 className="ev-form__section-title">Ticket Tiers</h3>
          {errors.tiers && <span className="ev-error" style={{ marginBottom: 8, display: 'block' }}>{errors.tiers}</span>}
          {tiers.map((tier, i) => (
            <div className="ev-tier-row" key={i}>
              <div className="ev-form__grid ev-form__grid--tier">
                <Field label={`Tier ${i + 1} Name`}>
                  <input className="ev-input" value={tier.name} onChange={e => setTier(i, 'name', e.target.value)} placeholder="e.g. VIP, Regular" />
                </Field>
                <Field label="Price (₦)">
                  <input type="number" className="ev-input" value={tier.price} onChange={e => setTier(i, 'price', e.target.value)} placeholder="0" min={0} />
                </Field>
                <Field label="Quantity">
                  <input type="number" className="ev-input" value={tier.quantity} onChange={e => setTier(i, 'quantity', e.target.value)} placeholder="Unlimited" min={1} />
                </Field>
              </div>
              {tiers.length > 1 && (
                <button type="button" className="ev-btn ev-btn--ghost ev-tier-remove" onClick={() => removeTier(i)}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
          <button type="button" className="ev-btn ev-btn--outline" style={{ marginTop: 8 }} onClick={addTier}>
            <Plus size={14} /> Add Tier
          </button>
        </div>

        <div className="ev-form__actions">
          <button type="button" className="ev-btn ev-btn--outline" onClick={() => navigate(isEdit ? `/events/${eventId}` : '/events/list')}>
            Cancel
          </button>
          <button type="submit" className="ev-btn ev-btn--primary" disabled={loading}>
            {loading ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Event')}
          </button>
        </div>
      </form>
    </div>
  );
}
